import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Coins, Check, Loader2, Sparkles, Zap } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const ICONS = { mini: Coins, medium: Zap, business: Sparkles };
const HIGHLIGHT = "medium";

export default function TokenShop() {
  const { user, refreshUser } = useAuth();
  const [packs, setPacks] = useState([]);
  const [balance, setBalance] = useState(user?.tokens_balance ?? 0);
  const [buying, setBuying] = useState("");
  const [params, setParams] = useSearchParams();
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    api.get("/tokens/packs").then((r) => setPacks(r.data.packs)).catch(() => {});
    api.get("/tokens/balance").then((r) => setBalance(r.data.tokens_balance)).catch(() => {});
  }, []);

  const pollStatus = useCallback(async (sid, tries = 0) => {
    if (tries > 6) { setVerifying(false); toast.error("Plata nu a fost confirmată încă. Verifică din nou în câteva momente."); return; }
    try {
      const r = await api.get(`/checkout/status/${sid}`);
      if (r.data.payment_status === "paid") {
        const b = await api.get("/tokens/balance");
        setBalance(b.data.tokens_balance);
        refreshUser();
        setVerifying(false);
        toast.success("Plată reușită! Tokenurile au fost adăugate în contul tău.");
        return;
      }
    } catch { /* keep polling */ }
    setTimeout(() => pollStatus(sid, tries + 1), 2000);
  }, [refreshUser]);

  useEffect(() => {
    const sid = params.get("session_id");
    if (sid) {
      setVerifying(true);
      pollStatus(sid);
      params.delete("session_id");
      setParams(params, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const buy = async (pack) => {
    if (!user) { toast.error("Autentifică-te pentru a cumpăra tokenuri."); return; }
    setBuying(pack);
    try {
      const r = await api.post("/tokens/checkout", { pack, origin_url: window.location.origin, referral_id: localStorage.getItem("oxo_ref") || null });
      window.location.href = r.data.url;
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); setBuying(""); }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-12" data-testid="token-shop-page">
      <div className="flex items-end justify-between gap-4 flex-wrap mb-10">
        <div>
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] font-bold text-orange-600 mb-2">
            <Coins className="w-3.5 h-3.5" /> OXO Tokenuri
          </div>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight">Reîncarcă-ți contul de tokenuri</h1>
          <p className="text-slate-500 mt-2 max-w-2xl">Folosește tokenuri pentru consultanța AI, Premium Listing și funcții avansate. Cumperi o dată, folosești oricând.</p>
        </div>
        <span className="inline-flex items-center gap-2 border border-slate-300 px-4 py-2 font-bold text-slate-700" data-testid="shop-balance">
          <Coins className="w-4 h-4 text-amber-500" /> {verifying ? <Loader2 className="w-4 h-4 animate-spin" /> : `${balance} tokenuri`}
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {packs.map((p) => {
          const Icon = ICONS[p.pack] || Coins;
          const hot = p.pack === HIGHLIGHT;
          return (
            <div key={p.pack} className={`relative border bg-white p-7 flex flex-col ${hot ? "border-orange-500 shadow-lg" : "border-slate-200"}`} data-testid={`pack-${p.pack}`}>
              {hot && <span className="absolute -top-3 left-7 bg-orange-600 text-white text-[10px] font-bold uppercase tracking-wider px-3 py-1">Cel mai popular</span>}
              <div className={`w-12 h-12 flex items-center justify-center mb-4 ${hot ? "bg-orange-600 text-white" : "bg-slate-900 text-white"}`}><Icon className="w-6 h-6" /></div>
              <h3 className="font-black text-xl">{p.label}</h3>
              <div className="mt-3 flex items-end gap-1">
                <span className="text-4xl font-black tracking-tight">${p.price}</span>
              </div>
              <div className="mt-4 space-y-2 text-sm text-slate-600 flex-1">
                <div className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-600" /> <b className="text-slate-900">{p.credits} credite</b> în total</div>
                <div className="flex items-center gap-2 text-slate-500 pl-6">{p.base_credits} de bază + {p.boost_credits} bonus</div>
                <div className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-600" /> Consultanță AI & Premium Listing</div>
                <div className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-600" /> Fără expirare</div>
              </div>
              <button onClick={() => buy(p.pack)} disabled={!!buying}
                className={`mt-6 inline-flex items-center justify-center gap-2 px-5 py-3 text-sm font-bold uppercase tracking-wide transition-colors disabled:opacity-50 ${hot ? "bg-orange-600 hover:bg-orange-700 text-white" : "bg-slate-900 hover:bg-slate-800 text-white"}`}
                data-testid={`buy-${p.pack}`}>
                {buying === p.pack ? <Loader2 className="w-4 h-4 animate-spin" /> : <Coins className="w-4 h-4" />} Cumpără
              </button>
            </div>
          );
        })}
      </div>
      <p className="text-[11px] text-slate-400 mt-6">Plăți securizate prin Stripe. Prețurile sunt în USD.</p>
    </div>
  );
}
